import { useEffect, useRef, useState } from "react";

/** Nextjs */
import dynamic from "next/dynamic";
import { useRouter } from "next/router";

/** MUI */
import {
    Fab,
    Box,
    Drawer,
    IconButton,
    Stack,
    Typography,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Divider,
    useMediaQuery,
    MenuList,
    MenuItem,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
/** - icons */
import {
    Close,
    CloudSyncOutlined,
    CloudUploadOutlined,
    CloudDownloadOutlined,
    CloudOutlined,
    SettingsSystemDaydreamOutlined,
} from "@mui/icons-material";

/** Helpers */
import { useTranslation } from "react-i18next";
import useHelper from "../../../_common/useHelper";
import clsx from "clsx";

/** redux */
import { useSelector } from "react-redux";
import { getWithRepository } from "@/src/_appStore/Common";

/** UI */
const AppTooltip = dynamic(() => import("../../../_common/AppTooltip"));
const ExportToRepository = dynamic(() => import("./ExportToRepository"));
const ImportFromRepository = dynamic(() => import("./ImportFromRepository"));

const useStyles = makeStyles((theme) => ({
    dialogTitle: {
        fontSize: "1.3333333rem",
        fontWeight: 600,
        [theme.breakpoints.down("lg")]: {
            fontSize: "1.11111111rem",
        },
    },
    boxedWrapper: {
        ...theme.shape.boxedWrapper,
        width: "100vw",
        maxWidth: 485,
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
        maxHeight: 272, // 552,
        top: "50%",
        transform: "translateY(-50%)!important",
        [theme.breakpoints.down("md")]: {},
        [theme.breakpoints.down("sm")]: {
            maxWidth: 385,
            maxHeight: 240,
        },
    },
    iconized: {
        padding: theme.spacing(0, 0, 0, 0),
        borderTopRightRadius: 20,
        borderBottomRightRadius: 20,
        height: "auto",
        maxHeight: 272, // 552,
        top: 163,
        right: 38,
        transform: "translateY(0%)!important",
        [theme.breakpoints.down("md")]: {},
        [theme.breakpoints.down("sm")]: {
            maxWidth: 385,
            maxHeight: 240,
        },
    },
    preloader: {
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        backgroundColor: "rgba(255, 255, 255, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "& img": {
            opacity: 0.5,
            width: 128,
            height: 128,
        },
    },
    title: {
        fontSize: "1.11111111rem",
        fontWeight: 600,
        [theme.breakpoints.down("lg")]: {
            fontSize: "1rem",
        },
    },
    stack: {
        paddingLeft: theme.spacing(2),
        flexGrow: 1,
        position: "relative",
        [theme.breakpoints.down("sm")]: {
            paddingLeft: theme.spacing(0),
        },
    },
    formControlLabelRoot: {
        [theme.breakpoints.down("lg")]: {
            marginLeft: theme.spacing(0),
            marginTop: theme.spacing(1),
        },
    },
    label: {
        [theme.breakpoints.down("lg")]: {
            fontSize: "0.88888889rem",
        },
    },
    labelOptions: {
        [theme.breakpoints.down("lg")]: {
            fontSize: "0.88888889rem",
        },
        [theme.breakpoints.down("sm")]: {
            fontSize: "0.77777778rem",
        },
    },
    header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: theme.spacing(0, 1),
        // marginBottom: theme.spacing(1),
        width: "100%",
        position: "relative",
        color: theme.palette.primary.main,
    },
    headerIconized: {
        padding: theme.spacing(1, 1, 1, 2),
        backgroundColor: theme.palette.primary.main,
        color: "#FFF",
    },
}));

const RepositoryDrawer = (props) => {
    const { open, onClose, source, icon = false, hideExport } = props;

    const classes = useStyles();
    const { t, i18n } = useTranslation();
    const { translationFromJson } = useHelper();
    const router = useRouter();
    const closeBtnRef = useRef();
    const rootBtnRef = useRef();

    const downSm = useMediaQuery((theme) => theme.breakpoints.down("sm"));
    const downLg = useMediaQuery((theme) => theme.breakpoints.down("lg"));

    /** States */
    const [OPEN, setOPEN] = useState(false);
    const [ERROR, setERROR] = useState(false);
    const [LOADING, setLOADING] = useState(false);
    const [EXPORT, setEXPORT] = useState(false);
    const [IMPORT, setIMPORT] = useState(false);
    const [ITEMS, setITEMS] = useState(null);
    /** redux */
    const withRepository = useSelector(getWithRepository);

    /** effects */
    useEffect(() => {
        setOPEN(open);
    }, [open]);

    /** Handlers */
    const toggleDrawer = (event) => {
        if (event?.type === "keydown" && (event?.key === "Tab" || event?.key === "Shift")) {
            return;
        }
        onClose && onClose();
        setOPEN(false);
    };

    const handleExport = (args) => {
        setEXPORT(args);
        (open || OPEN) && toggleDrawer();
    };

    const handleOnCloseExport = () => {
        handleExport(false);
        setTimeout(() => {
            rootBtnRef?.current?.focus();
        }, 100);
    };

    const handleImport = (args) => {
        setIMPORT(args);
        (open || OPEN) && toggleDrawer();
    };

    const handleOnCloseImport = () => {
        handleImport(false);
        setTimeout(() => {
            rootBtnRef?.current?.focus();
        }, 100);
    };

    const handleGotoRepository = () => {
        router.push(`/${i18n.language}/repozytorium`);
    };

    const handleOnGetItems = (args) => {
        setITEMS(args);
    };

    const handleLastItemKeyDown = (event) => {
        if (event?.key === "Tab" && !event?.shiftKey) {
            event.preventDefault();
            event.stopPropagation();
            closeBtnRef?.current?.focus();
        }
    };

    return withRepository ? (
        <>
            {!!icon && !OPEN && (
                <AppTooltip title={t("Repozytorium")} arrow placement={"top-end"} type={"dark"}>
                    <Fab
                        id={"repository-actions"}
                        ref={rootBtnRef}
                        color={"primary"}
                        onClick={() => {
                            setOPEN(true);

                            var a = document.querySelector("#__app-page-announcer__");
                            if (a) {
                                a.innerHTML = " ";
                                a.setAttribute("aria-live", "off");
                            }
                        }}
                        sx={{ position: "fixed", right: 38, top: 163, zIndex: 2100, width: 51, height: 51 }}
                    >
                        <CloudSyncOutlined />
                    </Fab>
                </AppTooltip>
            )}
            <Drawer
                anchor={"right"}
                open={OPEN}
                onClose={toggleDrawer}
                classes={{ paper: clsx(classes.boxedWrapper, !!icon && classes.iconized) }}
            >
                <Box className={clsx(classes.header, !!icon && classes.headerIconized)}>
                    <Box
                        sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", flexGrow: 1, pl: 1 }}
                        inert={"true"}
                    >
                        <Fab
                            component={"span"}
                            color={"primary"}
                            sx={{
                                width: 51,
                                height: 51,
                                bgcolor: (theme) => (!!icon ? theme.palette.primary.main : "#FFF"),
                                boxShadow: "none",
                                ml: -1,
                                mr: 2,
                            }}
                        >
                            <CloudSyncOutlined fontSize={downLg ? "small" : "medium"} />
                        </Fab>
                        <Typography
                            variant={"body1"}
                            component={"h2"}
                            className={classes.dialogTitle}
                            id={"repo-drawer-title"}
                        >
                            {t("Repozytorium")}
                        </Typography>
                    </Box>

                    <AppTooltip title={t("Zamknij okno")} arrow placement={"top-end"} type={"dark"}>
                        <IconButton
                            onClick={toggleDrawer}
                            aria-label={t("Zamknij okno")}
                            color={"inherit"}
                            ref={closeBtnRef}
                        >
                            <Close color={"inherit"} fontSize={downLg ? "small" : "medium"} />
                        </IconButton>
                    </AppTooltip>
                </Box>
                <Stack
                    direction="column"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    spacing={2}
                    className={classes.stack}
                >
                    <Box sx={{ width: "100%" }}>
                        <MenuList autoFocusItem={OPEN}>
                            <MenuItem
                                onClick={() => handleExport(true)}
                                disabled={hideExport}
                                tabIndex={0}
                                sx={{ py: 1 }}
                                aria-roledescription={t("element menu klikalny")}
                            >
                                <ListItemIcon>
                                    <CloudUploadOutlined />
                                </ListItemIcon>
                                <ListItemText primary={t("Dodaj do zestawień")} />
                            </MenuItem>
                            <MenuItem
                                onClick={() => handleImport(true)}
                                tabIndex={0}
                                sx={{ py: 1 }}
                                aria-roledescription={t("element menu klikalny")}
                            >
                                <ListItemIcon>
                                    <CloudDownloadOutlined />
                                </ListItemIcon>
                                <ListItemText primary={t("Wczytaj z zestawień")} />
                                {/* <ListItemIcon sx={{ minWidth: "unset" }}>{`(${ITEMS?.count})`}</ListItemIcon> */}
                            </MenuItem>
                            <Divider />
                            <MenuItem
                                // href={`/${i18n.language}/repozytorium`}
                                onClick={handleGotoRepository}
                                tabIndex={0}
                                onKeyDown={handleLastItemKeyDown}
                                aria-roledescription={t("element menu link")}
                            >
                                <ListItemIcon>
                                    <SettingsSystemDaydreamOutlined />
                                </ListItemIcon>
                                <ListItemText primary={t("Wszystkie zestawienia")} />
                            </MenuItem>
                        </MenuList>
                    </Box>
                </Stack>

                {!!LOADING ? (
                    <Box className={classes.preloader}>
                        <img src="/images/preloader_CircleV01.gif" alt="loading..." />
                    </Box>
                ) : null}
            </Drawer>

            {!!EXPORT && (
                <ExportToRepository
                    open={EXPORT}
                    // onClose={() => handleExport(false)}
                    onClose={handleOnCloseExport}
                    source={source}
                />
            )}

            {!!IMPORT && (
                <ImportFromRepository
                    open={IMPORT}
                    // onClose={() => handleImport(false)}
                    onClose={handleOnCloseImport}
                    source={source}
                    onGetItems={handleOnGetItems}
                />
            )}
        </>
    ) : null;
};

export default RepositoryDrawer;
